import { validateEmail } from "./helpers";
import {
  getProfileById,
  resolveColumnMapping,
  detectPlatform,
} from "./csvPlatforms";

/**
 * RFC 4180-compliant CSV parser.
 * Handles: quoted fields with commas, escaped quotes (""), quoted newlines.
 *
 * @param {string} csvText - Raw CSV text
 * @returns {{ headers: string[], rows: string[][] }}
 */
function parseCSVRaw(csvText) {
  const rows = [];
  let currentRow = [];
  let currentField = "";
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        currentField += '"';
        i++; // skip escaped quote
      } else if (char === '"') {
        inQuotes = false;
      } else {
        currentField += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        currentRow.push(currentField.trim());
        currentField = "";
      } else if (char === "\n" || (char === "\r" && nextChar === "\n")) {
        currentRow.push(currentField.trim());
        if (currentRow.some((f) => f !== "")) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = "";
        if (char === "\r") i++; // skip \n after \r
      } else if (char === "\r") {
        // bare \r (old Mac line ending)
        currentRow.push(currentField.trim());
        if (currentRow.some((f) => f !== "")) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = "";
      } else {
        currentField += char;
      }
    }
  }

  // Handle last field/row
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    if (currentRow.some((f) => f !== "")) {
      rows.push(currentRow);
    }
  }

  if (rows.length === 0) {
    return { headers: [], rows: [] };
  }

  const headers = rows[0].map((h) =>
    h
      .toLowerCase()
      .replace(/['"]/g, "")
      .trim(),
  );
  return { headers, rows: rows.slice(1) };
}

/**
 * Normalize various date formats to YYYY-MM-DD.
 * Handles: YYYY-MM-DD, YYYY/MM/DD, MM/DD/YYYY, MM-DD-YYYY, and Date-parseable strings.
 *
 * @param {string} dateStr - Date string in any common format
 * @returns {string} Normalized YYYY-MM-DD string, or original string if unparseable
 */
function normalizeDate(dateStr) {
  if (!dateStr) return "";
  const trimmed = dateStr.trim();

  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  // YYYY/MM/DD (SportsEngine)
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(trimmed))
    return trimmed.replace(/\//g, "-");

  // MM/DD/YYYY or MM-DD-YYYY
  const mdyMatch = trimmed.match(
    /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/,
  );
  if (mdyMatch) {
    return `${mdyMatch[3]}-${mdyMatch[1].padStart(2, "0")}-${mdyMatch[2].padStart(2, "0")}`;
  }

  // Fall through: attempt Date constructor
  const d = new Date(trimmed);
  if (!isNaN(d.getTime())) {
    return d.toISOString().split("T")[0];
  }

  return trimmed; // return as-is; validation will catch it
}

/**
 * Parse a CSV file with platform-aware column mapping.
 *
 * @param {string} csvText - Raw CSV text
 * @param {string|null} evaluationId - Optional evaluation ID to attach to each player
 * @param {string} platformId - Platform ID ("generic", "teamsnap", "sportsengine", "crossbar", "leagueapps", "auto")
 * @returns {{ players: object[], errors: string[], columnMapping: object|null, detectedPlatform: string|null, filteredRowCount: number }}
 */
export function parseCSV(csvText, evaluationId = null, platformId = "generic") {
  const { headers, rows } = parseCSVRaw(csvText);

  if (rows.length === 0) {
    return {
      players: [],
      errors: ["CSV file must contain at least a header row and one data row"],
      columnMapping: null,
      detectedPlatform: null,
      filteredRowCount: 0,
    };
  }

  // Platform detection
  let resolvedPlatformId = platformId;
  if (!platformId || platformId === "auto") {
    resolvedPlatformId = detectPlatform(headers);
  }
  const profile = getProfileById(resolvedPlatformId);
  const mapping = resolveColumnMapping(headers, profile);

  // Detect unmapped duplicate email columns (e.g. "Email,Email,Email,Email")
  // The first "email" header maps to the player's email field; extras become parent emails.
  const extraEmailIndices = [];
  if (!mapping.parent_emails) {
    const emailCandidates = new Set(profile.columnMap.email || []);
    const usedByMapping = new Set(
      Object.values(mapping).filter(Boolean).map((m) => m.headerIndex),
    );
    headers.forEach((h, i) => {
      if (emailCandidates.has(h) && !usedByMapping.has(i)) {
        extraEmailIndices.push(i);
      }
    });
  }

  // Build a headerIndex lookup for row filters
  const headerIndex = {};
  headers.forEach((h, i) => {
    headerIndex[h] = i;
  });

  // Validate that we can extract names
  if (!mapping.name && !mapping.first_name && !mapping.last_name) {
    return {
      players: [],
      errors: [
        "Missing required columns: either 'Name' or 'First_Name'/'Last_Name'",
      ],
      columnMapping: mapping,
      detectedPlatform: resolvedPlatformId,
      filteredRowCount: 0,
    };
  }

  const players = [];
  const errors = [];
  const usedNumbers = new Set();
  let filteredRowCount = 0;

  for (let i = 0; i < rows.length; i++) {
    const values = rows[i];
    const rowNum = i + 2; // 1-indexed, header is row 1

    // Apply row filter
    if (profile.rowFilter && !profile.rowFilter(values, headerIndex)) {
      filteredRowCount++;
      continue;
    }

    // Extract fields via mapping
    const getMapped = (field) => {
      const m = mapping[field];
      return m ? (values[m.headerIndex] || "").trim() : "";
    };

    const rowErrors = [];

    const name = getMapped("name");
    const firstName = getMapped("first_name");
    const lastName = getMapped("last_name");
    const numberStr = getMapped("number");
    const position = getMapped("position");
    const gradeStr = getMapped("grade");
    const gradYearStr = getMapped("grad_year");
    let dobStr = getMapped("dob");
    const email = getMapped("email");
    const parentEmailStr = getMapped("parent_emails");
    const notes = getMapped("notes");
    const group = getMapped("group");

    // Handle name parsing
    let finalFirstName = firstName;
    let finalLastName = lastName;

    if (!firstName && !lastName && name) {
      const nameParts = name.trim().split(/\s+/);
      finalFirstName = nameParts[0] || "";
      finalLastName = nameParts.slice(1).join(" ") || "";
    }

    // Validate name
    if (!finalFirstName && !finalLastName && !name) {
      rowErrors.push(`Row ${rowNum}: Name (or First_Name/Last_Name) is required`);
    }

    // Validate number (optional)
    let number = null;
    if (numberStr) {
      const parsedNumber = parseInt(numberStr);
      if (isNaN(parsedNumber) || parsedNumber < 1 || parsedNumber > 99) {
        rowErrors.push(
          `Row ${rowNum}: Invalid jersey number (must be 1-99)`,
        );
      } else if (usedNumbers.has(parsedNumber)) {
        rowErrors.push(
          `Row ${rowNum}: Duplicate jersey number ${parsedNumber}`,
        );
      } else {
        usedNumbers.add(parsedNumber);
        number = parsedNumber;
      }
    }

    // Validate grade (optional)
    let grade = null;
    if (gradeStr) {
      grade = parseInt(gradeStr);
      if (isNaN(grade) || grade < 1 || grade > 12) {
        rowErrors.push(`Row ${rowNum}: Grade must be 1-12`);
      }
    }

    // Validate grad year (optional)
    let gradeYear = null;
    if (gradYearStr) {
      gradeYear = parseInt(gradYearStr);
      if (isNaN(gradeYear) || gradeYear < 2020 || gradeYear > 2035) {
        rowErrors.push(
          `Row ${rowNum}: Graduation year must be between 2020-2035`,
        );
      }
    }

    // Normalize and validate date of birth (optional)
    let dob = null;
    if (dobStr) {
      // Apply platform-specific date transform first
      if (profile.dateTransform) {
        dobStr = profile.dateTransform(dobStr);
      }
      // Then generic normalization
      dobStr = normalizeDate(dobStr);

      const parsedDate = new Date(dobStr);
      if (isNaN(parsedDate.getTime())) {
        rowErrors.push(
          `Row ${rowNum}: Invalid date of birth format`,
        );
      } else {
        dob = dobStr;
      }
    }

    // Validate email (optional)
    if (email && !validateEmail(email)) {
      rowErrors.push(`Row ${rowNum}: Invalid email format`);
    }

    if (rowErrors.length > 0) {
      errors.push(...rowErrors);
    } else if (finalFirstName || finalLastName) {
      // Collect parent emails from mapped column or duplicate email columns
      let parentEmails = [];
      if (parentEmailStr) {
        parentEmails = parentEmailStr.split(/[,;]+/).map((e) => e.trim()).filter((e) => e && validateEmail(e));
      } else if (extraEmailIndices.length > 0) {
        parentEmails = extraEmailIndices
          .map((idx) => (values[idx] || "").trim())
          .filter((e) => e && validateEmail(e));
      }

      const player = {
        first_name: finalFirstName || "",
        last_name: finalLastName || "",
        position: position || "",
        grade,
        grad_year: gradeYear,
        dob,
        email: email || "",
        parent_emails: parentEmails,
        notes: notes || "",
        group: group || "",
      };

      if (number !== null) {
        player.number = number.toString();
      }

      if (evaluationId) {
        player.evaluation_id = evaluationId;
      }

      players.push(player);
    }
  }

  return {
    players,
    errors,
    columnMapping: mapping,
    detectedPlatform: resolvedPlatformId,
    filteredRowCount,
  };
}
